// The model each agent uses by default (Features 2, 21 Oct): one for Claude, one for Codex, chosen in Settings,
// Agents, or with Set as default in the chat's model menu (Features 4, 26 Oct), and kept on the board, so every device,
// a new chat, a job, a brief and a Button start on it. A chat, a Button or a job can have a model of its own instead.
// One rule for all of them, here, so the worker (jobs and briefs) and the browser (the chat and Buttons) agree.

export type AgentId = "claude" | "codex";

/** Whether a model id is one of that agent's: Claude's start claude-, Codex's gpt-. Newer ones than this build knows pass. */
export const modelFitsAgent = (agent: AgentId, id: unknown): id is string => typeof id === "string" && (agent === "claude" ? /^claude-[a-z0-9.-]{2,40}$/ : /^gpt-[a-z0-9.-]{1,40}$/).test(id);

/** The model each agent starts on until the user chooses one: the chat, a Button and a job alike. */
export const BUILT_IN: Record<AgentId, string> = { claude: "claude-opus-5", codex: "gpt-6.1-sol" };

/** The models this build lists for each agent, as the chat's menu shows them (src/agent/config.ts keeps their details). */
export const KNOWN: Record<AgentId, readonly string[]> = {
  claude: ["claude-fable-5-1", "claude-opus-5-5", "claude-opus-5", "claude-sonnet-5", "claude-haiku-4-5"],
  codex: ["gpt-6-astra", "gpt-6.1-sol", "gpt-6-luna"],
};

/** The defaults as the board keeps them; listed names a default newer than this build that the provider's own list showed. */
export interface ChosenModels {
  claude?: string;
  codex?: string;
  listed?: string[];
}

/**
 * Whether the board knows a model: one this build lists, a dated release of one (claude-opus-5-20261001), or one the
 * provider's own model list showed (also). Never a near match: claude-opus-5-5 is not claude-opus-5.
 */
export function knownModel(agent: AgentId, id: unknown, also: readonly string[] = []): id is string {
  if (!modelFitsAgent(agent, id)) return false;
  if (KNOWN[agent].includes(id) || also.includes(id)) return true;
  const dated = /^(.+)-\d{8}$/.exec(id);
  return !!dated && KNOWN[agent].includes(dated[1]);
}

/** What the default is for an agent: the one chosen, whether the board knows it, and whether it was chosen at all. */
export function defaultFor(agent: AgentId, chosen: ChosenModels | null | undefined, also: readonly string[] = []): { id: string; known: boolean; chosen: boolean } {
  const id = chosen?.[agent];
  if (!modelFitsAgent(agent, id)) return { id: BUILT_IN[agent], known: true, chosen: false };
  return { id, known: knownModel(agent, id, [...also, ...(chosen?.listed ?? [])]), chosen: true };
}

/**
 * The model a run starts on: its own (a chat's, a Button's or a job's) when the board knows it, else the agent's
 * default. A default the board does not know, one the agent no longer offers or newer than this build, is never
 * swapped for a model nobody chose: null, and the agent runs on its own default. An unknown model of a run's own
 * goes back to the default, which the user chose more lately than it.
 */
export function runModel(agent: AgentId, chosen: ChosenModels | null | undefined, own?: unknown, also: readonly string[] = []): string | null {
  const seen = [...also, ...(chosen?.listed ?? [])];
  if (knownModel(agent, own, seen)) return own;
  const d = defaultFor(agent, chosen, also);
  return d.known ? d.id : null;
}
